<template>
  <div class="relative" ref="dropdownContainer">
    <button
      class="flex items-center justify-between w-full px-2 py-1 border md:py-1 md:text-base"
      :class="{
        'border-[#ccc] text-gray-500 font-light': !selectedMenuItem,
        [`rounded-${radius} h-${height}`]: true,
        'bg-[#F7F6FB] font-light rounded-lg border-primary text-primary':
          selectedMenuItem,
      }"
      type="button"
      @click="toggleDropdown"
    >
      <span class="truncate">{{ selectedMenuItem || buttonLabel }}</span>
      <DropdownIconSvg
        color="black"
        height="4"
        width="4"
        :class="{ 'rotate-180': isDropdownOpen }"
      />
    </button>
    <!-- Dropdown menu -->
    <div
      v-show="isDropdownOpen"
      class="md:top-[60px] absolute top-full left-0 md:z-50 z-[9999] divide-y divide-gray-100 shadow rounded-md w-full text-black bg-white text-base overflow-y-auto max-h-44 md:w-48"
    >
      <ul class="py-1 text-gray-700">
        <li v-for="(item, index) in menuItems" :key="index">
          <button
            class="block w-full px-2 py-1 md:py-2 md:px-4 hover:bg-gray-100 text-start"
            :class="{
              'bg-primary': selectedMenuItem === item,
              'border-gray-400': !selectedMenuItem,
              'border-secondary': selectedMenuItem,
            }"
            @click.stop="handleMenuItemClick(item)"
          >
            <span class="font-light capitalize">{{ item[itemName] }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import DropdownIconSvg from "@/components/svg/DropDownIconSvg.vue";
import { ref, onMounted, onUnmounted, watch } from "vue";

export default {
  components: {
    DropdownIconSvg,
  },
  props: {
    buttonLabel: {
      type: String,
      required: true,
    },
    menuItems: {
      type: Object,
      required: true,
    },
    radius: {
      type: String,
      default: "md",
    },
    itemName: {
      type: String,
      default: "name",
    },
    height: {
      type: String,
      default: "[48px]",
    },
    uniqueIdentifier: {
      type: String,
      required: false,
    },
  },
  setup(props, { emit }) {
    const dropdownContainer = ref(null);
    const isDropdownOpen = ref(false);
    const selectedMenuItem = ref(null);

    /**
     * Toggles the visibility of the dropdown menu.
     */
    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };

    /**
     * Closes the dropdown menu.
     */
    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };

    /**
     * Handles click events outside the dropdown container.
     * @param {Event} event - The click event object.
     */
    const handleClickOutside = (event) => {
      if (
        dropdownContainer.value &&
        !dropdownContainer.value.contains(event.target)
      ) {
        closeDropdown();
      }
    };

    /**
     * Handles the selection of a menu item.
     * @param {Object} item - The selected menu item object.
     */
    const handleMenuItemClick = (item) => {
      selectedMenuItem.value = item.name;
      closeDropdown();
      emit("menu-item-selected", {
        itemId: item.id ?? item.value,
        label: item.name,
        dropdownIdentifier: props.uniqueIdentifier,
      });
    };

    // Add and remove click event listener on mount and unmount
    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });
    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    // Close the dropdown when menu items change
    watch(() => props.menuItems, closeDropdown);

    return {
      dropdownContainer,
      isDropdownOpen,
      toggleDropdown,
      closeDropdown,
      handleMenuItemClick,
      selectedMenuItem,
    };
  },
};
</script>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
}
.dropdown-menu {
  overflow-y: auto;
  max-height: 200px;
}

@media (max-width: 768px) {
  .dropdown-menu {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 50vh;
    border-radius: 1rem 1rem 0 0;
  }
}
</style>
